import { useState, useEffect, useCallback } from 'react';
import { useOktaAuth } from '@okta/okta-react';

/**
 * useSecureOktaUser
 * Hook sécurisé pour récupérer l'utilisateur Okta uniquement quand:
 *  - authState.isAuthenticated est vrai
 *  - un accessToken est présent (flow code+PKCE, storage mémoire)
 * Fournit états et helpers pour rendre conditionnellement l'UI.
 *
 * Returns:
 *  {
 *    user: object | null,
 *    isAuthenticated: boolean,
 *    loading: boolean,          // true tant que la récupération est en cours ou en attente de token
 *    error: Error | null,
 *    refresh: () => void        // force une tentative de reload user
 *  }
 */
export function useSecureOktaUser(options = {}) {
  const { skip = false } = options;
  const { authState, oktaAuth } = useOktaAuth();
  const [user, setUser] = useState(null);
  // loading: vrai uniquement quand une requête user est en cours
  const [loading, setLoading] = useState(false);
  // attempted: empêche les relances infinies automatiques en cas d'erreur
  const [attempted, setAttempted] = useState(false);
  const [error, setError] = useState(null);
  const isAuthenticated = !!authState?.isAuthenticated;

  const canLoad = isAuthenticated && !!authState?.accessToken?.accessToken;

  const loadUser = useCallback(async () => {
    if (skip) return;
    if (!canLoad) return; // attendre conditions
    setLoading(true);
    setError(null);
    try {
      const u = await oktaAuth.getUser();
      setUser(u);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [canLoad, oktaAuth, skip]);

  // Effet unique gérant l'initialisation ou les changements d'état d'authentification
  useEffect(() => {
    if (!isAuthenticated) {
      setUser(null);
      setAttempted(false); // nouvelle tentative possible après re-auth
      return;
    }
    if (!skip && canLoad && user == null && !attempted) {
      setAttempted(true);
      loadUser();
    }
  }, [isAuthenticated, canLoad, user, skip, attempted, loadUser]);

  const refresh = useCallback(() => {
    if (canLoad) {
      loadUser();
    }
  }, [canLoad, loadUser]);

  return { user, isAuthenticated, loading: skip ? false : loading, error, refresh };
}

export default useSecureOktaUser;
